import type { InferGetServerSidePropsType } from 'next'
import Image from 'next/image'
import Page from '@/components/page'
import PageMetadata from '@/components/metadata'
import { getFooter, graphQLClient } from 'src/lib/graphql-contentful-api'
import {
  ComponentRenderer,
  ComposeComponentImage_Fragment,
  ComposeComponentVideoAndAdjacentText_Fragment,
} from 'src/lib/rich-text-render-helpers'
import { graphql } from '.gql/gql'
import { useFragment as getFragment } from '.gql/fragment-masking'
import invariant from 'tiny-invariant'
import { BlockQuote, Star } from '@marketing-sites/icons'
import bettAwards from '../../public/images/bett-awards.png'
import type { Document } from '@contentful/rich-text-types'
import type { ServerSideProps } from '@/pages/index'
import type { ComposeComponentVideoAndAdjacentTextFragment } from 'src/lib/rich-text-render-helpers'
import Link from 'next/link'
import Script from 'next/script'

const WritingLegendsContactUsPage_Query = graphql(/* GraphQL */ `
  query WritingLegendsMarketingSiteContactUsPageV2(
    $id: String!
    $locale: String
  ) {
    writingLegendsMarketingSiteContactUsPageV2(id: $id, locale: $locale) {
      sys {
        id
      }
      seoPageTitle
      seoPageDescription
      seoKeywords
      formLink
      contactUsVideoAndAdjacentText {
        ...ComposeComponentVideoAndAdjacentText_Fragment
      }
      productInfoBlock {
        json
      }
      phoneNumber
      email
      formTitle
      downloadSectionDescription
      downloadSectionLink
      downloadSectionButtonText
      downloadSectionImage {
        ...ComposeComponentImage_Fragment
      }
      customerTestimonial {
        quote
        author
        date
        rating
      }
    }
  }
`)

export const getServerSideProps = async ({ query, req }: ServerSideProps) => {
  invariant(query.locale, 'query.locale is required')

  const [{ data: footerData }, { data: contactUsData }] = await Promise.all([
    getFooter(query.locale),
    graphQLClient.query(WritingLegendsContactUsPage_Query, {
      id: '1n41p4kApLOXHMF99N7Cab',
      locale: query.locale,
    }),
  ])

  if (
    !footerData?.composeComponentWlFooterBlock ||
    !contactUsData?.writingLegendsMarketingSiteContactUsPageV2
  ) {
    return { notFound: true }
  }

  return {
    props: {
      footerProps: footerData.composeComponentWlFooterBlock,
      contactPageData: contactUsData.writingLegendsMarketingSiteContactUsPageV2,
      country: query.country,
      domain: req.headers.host,
    },
  }
}

interface VideoAndAdjacentTextProps {
  query: ComposeComponentVideoAndAdjacentTextFragment
}

function VideoAndAdjacentText({ query }: VideoAndAdjacentTextProps) {
  const { video, description } = getFragment(
    ComposeComponentVideoAndAdjacentText_Fragment,
    query,
  )

  return (
    <div className="bg-[#EEF9FF]">
      <div className="mx-auto my-6 grid max-w-screen-lg grid-cols-1 items-center gap-x-8 bg-[#EEF9FF] prose-h3:text-left prose-h3:text-2xl max-md:px-4 md:grid-cols-2 md:justify-items-center">
        <div className="h-auto w-full">
          <video
            className="rounded-2xl border-2 border-solid drop-shadow-md"
            controls
            src={video?.url ?? ''}
          />
        </div>
        <ComponentRenderer
          document={description?.json as Document}
          classNames="prose-h3:my-0 prose-h3:text-center prose-h3:text-xl md:pr-24 md:prose-h3:text-left md:prose-h3:text-4xl"
        />
      </div>
    </div>
  )
}

interface CustomerReviewCardProps {
  customerRating: number
  quote: string
  author: string
}

function CustomerReviewCard({
  customerRating,
  quote,
  author,
}: CustomerReviewCardProps) {
  return (
    <div className="not-prose relative mx-auto flex max-w-5xl flex-wrap justify-center rounded-2xl border border-solid border-re-neutral-base border-opacity-20 bg-white p-2 text-center font-open-sans text-lg text-re-text-base shadow-sm md:p-6">
      <div
        className="mt-4 flex flex-row"
        aria-label={`${customerRating.toString()} out of 5 stars`}
      >
        {Array.from({ length: customerRating }).map((_, index) => (
          <Star
            key={index}
            data-testid="testimonial-card-star"
            className="text-3xl text-re-accent-yellow-base"
          />
        ))}
      </div>
      <blockquote className="relative z-50 my-2">
        <BlockQuote className="absolute -top-5 -z-10 mb-4 text-5xl text-re-neutral-light opacity-50" />
        <p data-testid="testimonial-card-quote" className="mb-4">
          {quote}&quot;
        </p>
        <div className="inline-flex w-full items-center justify-center text-center">
          <cite
            data-testid="testimonial-card-author"
            className="font-semibold not-italic"
          >
            {author}
          </cite>
        </div>
      </blockquote>
    </div>
  )
}

interface DownloadWhitePaperProps {
  assetUrl: string
  buttonText: string
  description: string
  downloadLink: string
}

function DownloadWhitePaper({
  assetUrl,
  buttonText,
  description,
  downloadLink,
}: DownloadWhitePaperProps) {
  return (
    <div className="bg-[#EEF9FF]">
      <div className="mx-auto my-6 grid max-w-screen-xl items-center justify-center justify-items-center gap-8 px-6 prose-h3:my-0 prose-h3:text-center prose-h3:text-2xl max-md:px-4 md:grid-cols-2 md:justify-items-end md:prose-h3:text-left">
        <div className="m-8 h-auto w-1/2 rotate-3 rounded-lg border border-wl-blue-regatta-400 bg-white shadow-stack drop-shadow-md">
          <div className="mx-auto h-full w-full">
            <Image
              src={assetUrl}
              width={0}
              height={0}
              alt="writing legends stacked logo"
              className="h-full w-full rotate-3 object-contain p-4"
            />
          </div>
          <div
            data-testid="testimonial-card-stacked"
            className="absolute left-0 top-0 -z-10 h-full w-full rotate-6 rounded-lg border border-wl-blue-regatta-400 bg-white shadow-stack"
          />
        </div>
        <div className="flex h-full w-full flex-col items-center justify-center md:items-start md:pr-24">
          <h3 className="text-[#001744]">{description}</h3>
          <Link
            href={downloadLink}
            className="not-prose my-4 inline-block rounded-full bg-wl-blue-regatta-700 px-8 py-2 font-semibold text-white"
          >
            {buttonText}
          </Link>
        </div>
      </div>
    </div>
  )
}

interface ContactDetailsProps {
  email: string | null
  phoneNumber: string | null
}

function ContactDetails({
  email,
  phoneNumber,
}: ContactDetailsProps): React.JSX.Element | null {
  if (!email || !phoneNumber) return null
  return (
    <div className="bg-wl-blue-regatta-600 py-6">
      <div className="mx-auto max-w-screen-xl px-6 text-white">
        <div className="flex flex-col space-y-2 text-center md:flex-row md:items-center md:justify-between md:space-y-0 md:text-left">
          <div className="md:space-y-2">
            <span className="font-bold">
              Contact Us{' '}
              <span className="inline md:block">for more information</span>
            </span>
          </div>
          <div className="md:space-y-2">
            <a
              href={`mailto:${email}`}
              className="not-prose block underline md:text-right"
            >
              {email}
            </a>
            <a
              href={`tel:${phoneNumber}`}
              className="not-prose block underline md:text-right"
            >
              {phoneNumber}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

interface FormBlockProps {
  formLink: string | null
  formTitle: string | null
}

declare global {
  interface Window {
    globalFormData?: {
      useUC?: boolean
      replaceSelector?: string
      iframeContainerSelector?: string
      iframeSelector?: string
      iframeUrl: string
      loadingSelector?: string
      params: object
      framework?: string
    }
    initGlobalForm?(): void
  }
}

function SignupForm({ formLink, formTitle }: FormBlockProps) {
  const formUrlString = formLink
  const formUrlObj = new URL(formUrlString ?? '')
  const formUrlParams = Object.fromEntries(formUrlObj.searchParams)
  const formUrl = `${formUrlObj.origin}${formUrlObj.pathname}`

  window.globalFormData = {
    useUC: true,
    iframeContainerSelector: '#global-form-container',
    iframeSelector: `#wl-form[data-global-form]`,
    iframeUrl: formUrl,
    loadingSelector: '#global-form-container #loading-overlay',
    params: formUrlParams,
    framework: 'nextjs',
  }

  return (
    <div>
      <h2 className="my-0 font-bold">{formTitle}</h2>
      <div id="global-form-container" className="relative">
        <iframe
          id="wl-form"
          data-global-form
          className="border-0"
          style={{ height: '830px' }}
        ></iframe>
        {/* loading indicator */}
        <div
          id="loading-overlay"
          className="absolute left-0 top-[20px] z-10 flex h-[calc(100%-40px)] w-full items-start justify-center rounded-lg bg-white"
        >
          <div className="mt-20 flex items-center justify-center">
            <div className="relative h-24 w-24 animate-spin rounded-full border-4 border-l-wl-blue-regatta-700 md:h-48 md:w-48"></div>
          </div>
        </div>
      </div>
      <Script
        type="module"
        src="https://marketing-cdn.3plearning.com/docs/dev/form/global-form-iframe.js"
        onReady={() => {
          window.initGlobalForm?.()
        }}
      ></Script>
    </div>
  )
}

function ContactUs({
  footerProps,
  contactPageData,
  country,
  domain,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const {
    productInfoBlock,
    formLink,
    seoKeywords,
    contactUsVideoAndAdjacentText,
    seoPageDescription,
    seoPageTitle,
    downloadSectionButtonText,
    downloadSectionDescription,
    downloadSectionImage,
    downloadSectionLink,
    formTitle,
    email,
    phoneNumber,
    customerTestimonial,
  } = contactPageData

  const image = getFragment(
    ComposeComponentImage_Fragment,
    downloadSectionImage,
  )

  return (
    <>
      <PageMetadata
        description={seoPageDescription}
        keywords={seoKeywords}
        title={seoPageTitle}
      />
      <Page
        footerData={footerProps}
        country={country}
        domain={domain}
        classNames="text-[#001744]"
      >
        {contactUsVideoAndAdjacentText && (
          <VideoAndAdjacentText query={contactUsVideoAndAdjacentText} />
        )}
        <div className="grid-auto-cols-fr mx-auto mb-20 mt-10 grid max-w-screen-xl grid-cols-1 gap-x-12 gap-y-6 px-6 md:grid-cols-2">
          <div className="flex flex-col justify-between space-y-8">
            <ComponentRenderer
              document={productInfoBlock?.json as Document}
              classNames="prose-li:bulleted-list-coin flex-col space-y-8 prose-h2:my-0 prose-h2:text-left prose-h2:text-[#001744] prose-ul:pl-0 prose-li:list-none md:space-y-14"
            />
            {customerTestimonial?.author &&
              customerTestimonial.rating &&
              customerTestimonial.quote && (
                <CustomerReviewCard
                  author={customerTestimonial.author}
                  customerRating={customerTestimonial.rating}
                  quote={customerTestimonial.quote}
                />
              )}
          </div>
          <SignupForm formLink={formLink} formTitle={formTitle} />
        </div>
        {image?.imageAsset?.url &&
          downloadSectionButtonText &&
          downloadSectionDescription &&
          downloadSectionLink && (
            <DownloadWhitePaper
              assetUrl={image.imageAsset.url}
              buttonText={downloadSectionButtonText}
              description={downloadSectionDescription}
              downloadLink={downloadSectionLink}
            />
          )}
        <div className="inline-flex justify-center bg-[#001744]">
          <Image
            src={bettAwards}
            alt="bett awards logo"
            className="h-1/2 w-auto"
          />
        </div>
        <ContactDetails email={email} phoneNumber={phoneNumber} />
      </Page>
    </>
  )
}

export default ContactUs
